import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import client from '../api/client';
import type { User, RegisterPayload } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<User>;
  register: (payload: RegisterPayload) => Promise<User>;
  logout: () => void;
  isAuthenticated: boolean;
  // Re-fetch /auth/me using whatever token is currently in localStorage.
  // Flows that write the token outside login() (e.g. AcceptInvite) must call
  // this afterwards or the nav/role state stays stale (falls back to
  // 'viewer'). See App.tsx handleAuthSuccess.
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = useCallback(async (): Promise<void> => {
    try {
      const response = await client.get<User>('/auth/me');
      setUser(response.data);
      setError(null);
    } catch (err) {
      localStorage.removeItem('access_token');
      setUser(null);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<User> => {
      setLoading(true);
      setError(null);
      try {
        const response = await client.post<{ access_token: string; user: User }>(
          '/auth/login',
          { email, password }
        );
        const { access_token, user: userData } = response.data;
        localStorage.setItem('access_token', access_token);
        setUser(userData);
        return userData;
      } catch (err) {
        const errorMessage = (err as any)?.response?.data?.detail || 'Login failed';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const register = useCallback(
    async (payload: RegisterPayload): Promise<User> => {
      setLoading(true);
      setError(null);
      try {
        const response = await client.post<{ access_token: string; user: User }>(
          '/auth/register',
          payload
        );
        const { access_token, user: userData } = response.data;
        localStorage.setItem('access_token', access_token);
        setUser(userData);
        return userData;
      } catch (err) {
        const errorMessage = (err as any)?.response?.data?.detail || 'Registration failed';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const logout = useCallback((): void => {
    localStorage.removeItem('access_token');
    setUser(null);
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    refreshUser: fetchCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
